<template>
    <div>
        <hts-ui-tabs
            v-model="tab"
            :tabs="tabs" />

        <div v-if="tab === 'quote' && dataOrder">
            <div v-if="dataOrder.quoteData && dataOrder.createOrderFrom === 'CUSTOMER_REQUEST' && dataOrder.allotted === 0">
                <div class="flex justify-end">
                    <hts-form-button
                        icon-name="hi-pencil-square-icon"
                        type="button"
                        color="info"
                        @click="handleEditQuote">
                        Chỉnh sửa báo giá
                    </hts-form-button>
                </div>
            </div>
            <view-quote-price
                v-if="dataOrder.quoteData"
                :data-create-quote="dataOrder.quoteData"
                :only-view="true" />

            <div v-else>
                <div class="flex justify-end">
                    <hts-form-button
                        icon-name="hi-plus-icon"
                        type="button"
                        color="info"
                        @click="handleAddQuote">
                        Thêm báo giá
                    </hts-form-button>
                </div>
            </div>

            <hts-modal
                :show="addQuote || editQuote"
                :title="addQuote ? $t('labels.quoteAdd') : $t('labels.quoteEdit')"
                @hide="reset">
                <hts-form
                    class="space-y-6"
                    :rules="{ quote: 'required' }"
                    @submit="handleSubmitForm">
                    <div class="h-96 w-full">
                        <hts-form-select
                            v-model="quote"
                            v-model:text="quoteName"
                            :label="$t('labels.quote')"
                            prop="quote"
                            name="quote"
                            key-id="_id"
                            key-label="codeAndPrice"
                            :items="listQuote"
                            class="w-full"
                            create
                            required
                            filterable
                            :creatable="checkPermission(layoutPermissions.quote_add)"
                            @create-new="openCreateQuote" />
                    </div>

                    <div class="flex justify-end">
                        <hts-form-button
                            icon-name="hi-check-icon"
                            type="submit"
                            color="info">
                            {{ $t('labels.save') }}
                        </hts-form-button>
                    </div>
                </hts-form>
            </hts-modal>
        </div>
        <div v-if="tab === 'purchase-price' && dataOrder">
            <data-table-price
                v-if="dataOrder.quoteData"
                :data-quote="dataOrder.quoteData"
                :headers="headerCharges" />
        </div>

        <view-price-detail ref="viewPriceDetail" />
        <view-price-vendor ref="viewPriceVendor" />
        <quote-add
            ref="quoteAdd"
            :go-to-management-page="false"
            @refreshTables="addQuoteSuccess" />
    </div>
</template>
<script setup>
import actions from '../../../partner/-components/actions.vue'
import MarkupView from '../../../quote/-components/markup-view.vue'
import ViewPriceDetail from '../../../quote/-components/view-price-detail.vue'
import ViewPriceVendor from '../../../quote/-components/view-price-vendor.vue'
import ViewQuotePrice from '../../../quote/-components/view-quote-price.vue'
import DataTablePrice from '../../../quote/management/-components/data-table-price.vue'
import QuoteAdd from '../../../quote/quote-request/-components/add-quote.vue'
import AddQuoteToOrder from './add-quote-to-order.ts'
import { numberFormatter } from '~/pages/trucker-[id]/price/vendor/ignore/data'
import layoutPermissions from '~/data/permissions/layout-permissions'
import { checkPermission } from '@/utils/permission-check'

const { $formatters } = useNuxtApp()

const quoteAdd = ref()
const tab = ref('')

const props = defineProps({
  dataOrder: Object
})

const emits = defineEmits(['updateOrder'])

const { dataOrder } = toRefs(props)

const CAllotment = computed(() => {
  return dataOrder.value?.allotment || []
})

const { listQuote, searchQuoteByOrder, resetListQuote, handleAddQuoteToOrder, handleEditQuoteToOrder } = AddQuoteToOrder()

const viewPriceDetail = ref()
const viewPriceVendor = ref()

const quote = ref('')
const quoteName = ref('')
const addQuote = ref(false)
const editQuote = ref(false)
const reOpen = ref(() => {})

const openCreateQuote = () => {
  if (!dataOrder.value.quoteRequest) {
    process.client && $toast().error('Tạo báo giá không thành công do đơn hàng chưa có yêu cầu báo giá')
    return
  }

  const currentAddQuote = addQuote.value
  const currentEditQuote = editQuote.value

  reOpen.value = () => {
    addQuote.value = $lodash.cloneDeep(currentAddQuote)
    editQuote.value = $lodash.cloneDeep(currentEditQuote)
  }

  addQuote.value = false
  editQuote.value = false

  quoteAdd.value && quoteAdd.value.open(dataOrder.value.quoteRequest)
}

const addQuoteSuccess = async (data) => {
  if (!data) {
    return
  }
  await searchQuoteByOrder(dataOrder.value._id)
  quote.value = data._id || ''
  quoteName.value = data.code + ' - ' + ($formatters.formatCurrency(data.informationEditorPricingSelling.pricingRequest) || 0)

  reOpen.value()
}

const handleAddQuote = () => {
  reset()
  addQuote.value = true
  searchQuoteByOrder(dataOrder.value._id)
}

const handleEditQuote = () => {
  reset()
  quote.value = dataOrder.value.quote
  quoteName.value = dataOrder.value.codeAndPrice
  searchQuoteByOrder(dataOrder.value._id)
  editQuote.value = true
}

const handleSubmitForm = async () => {
  if (addQuote.value) {
    const result = await handleAddQuoteToOrder(dataOrder.value._id, {
      quoteId: quote.value,
      codeAndPrice: quoteName.value
    })

    if (result) {
      reset()
      emits('updateOrder', result)
    }
  }

  if (editQuote.value) {
    const result = await handleEditQuoteToOrder(dataOrder.value._id, {
      quoteId: quote.value,
      codeAndPrice: quoteName.value
    })

    if (result) {
      reset()
      emits('updateOrder', result)
    }
  }
}

const reset = () => {
  quote.value = ''
  quoteName.value = ''
  reOpen.value = () => {}
  addQuote.value = false
  editQuote.value = false
}

onUnmounted(() => {
  resetListQuote()
  reset()
})

const tabs = [
  {
    key: 'quote',
    label: $t('labels.quote'),
    active: true,
    permission: true
  },
  { key: 'purchase-price', label: $t('labels.purchasePrice'), permission: true }
]

const handleViewQuotePrice = (item) => {
  if (item.type === 'internal') {
    viewPriceDetail.value && viewPriceDetail.value.open(item, dataOrder.value.quoteData.priceOfflineFee)
  } else if (item.type === 'vendor') {
    viewPriceVendor.value && viewPriceVendor.value.open(item.priceVendorId)
  }
}

const headerCharges = [
  {
    field: 'fleetVehicle',
    headerName: $t('labels.fleetVehicle'),
    sortable: true,
    valueGetter: (params) => {
      return params.data.type === 'internal' ? $t('labels.internal') : params?.data?.vendorName
    }
  },
  {
    field: 'price',
    headerName: $t('labels.purchasePrice'),
    sortable: true,
    valueGetter: (params) => {
      return typeof params?.data?.pricingEstimate?.total === 'number' ? numberFormatter(params.data.pricingEstimate.total) : 0
    }
  },
  {
    field: 'markup',
    key: 'markup',
    headerName: $t('labels.markup'),
    editable: (params) => {
      return params.node.selected
    },
    autoHeight: true,
    width: 100,
    cellRenderer: MarkupView
  },
  {
    field: 'profit',
    headerName: $t('labels.profit'),
    sortable: true,
    valueGetter: (params) => {
      const profit = params.data.pricingEstimate?.profit
      return typeof profit === 'number' ? numberFormatter($lodash.ceil(profit, -3)) : 0
    }
  },
  {
    field: 'successAllocatedOutputTrip',
    headerName: $t('labels.successAllocatedOutputTrip'),
    sortable: true,
    valueGetter: (params) => {
      const type = params.data.type
      const vendorId = params.data.vendorId

      if (type === 'internal') {
        const item = CAllotment.value.filter((item) => item.type === 'internal')[0]

        return item?.productivity || ''
      }

      if (type === 'vendor' && vendorId) {
        const item = CAllotment.value.filter((item) => item.priceVendorId === vendorId)[0]
        return item?.productivity || ''
      }
      return ''
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: actions,
    cellRendererParams: {
      notCheck: true,
      items: [
        {
          icon: 'hi-pencil-square-icon',
          label: $t('labels.viewDetail'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: true,
          validate: (data) => {
            if (data.type === 'internal') {
              return checkPermission(layoutPermissions.quote_view_detail_quote_internal)
            } else if (data.type === 'vendor') {
              return checkPermission(layoutPermissions.price_vendor_management)
            } else {
              return false
            }
          },
          click: handleViewQuotePrice
        }
      ]
    }
  }
]
</script>
